import React, { useState, useEffect } from 'react';
import {
    HistoryContainer,
    HistoryWrapper,
    HistoryText,
    EmptyHistoryMessage,
    GroupTitle,
    ConversationGroup,
    LoadingContainer
} from './HistoryContent.styled';
import { observer } from "mobx-react";
import { useStore } from "../../../../hooks";
import { CircularProgress } from '@mui/material';
import { HistoryGroups } from '../../../../utils/enums';

const HistoryContent = observer(({ searchTerm = '', hasAgentMatches = false }) => {
    const { historyAgentStore, authStore } = useStore();
    const [isLoadingConversations, setIsLoadingConversations] = useState(false);
    const conversations = historyAgentStore.conversations;
    const selectedAgent = historyAgentStore.selectedAgent || {};
    const selectedConversationId = historyAgentStore.selectedConversationId;

    const handleConversationClick = (conversationId) => {
        // Set UI state immediately for responsive feedback
        historyAgentStore.setSelectedConversationId(conversationId);
        historyAgentStore.setLastConversationID(conversationId);
        historyAgentStore.setIsChatQuestionsVisible(false);
        historyAgentStore.setShouldRefreshMessages(true);
        // Fetch messages - isLoadingMessages will trigger spinner in HomePage
        historyAgentStore.fetchMessagesHTTP(conversationId);
    };

    const getFilteredConversations = () => {
        if (!conversations) return [];
        
        let filteredConversations = conversations
            .filter(item => item.agent_id === (selectedAgent.uuid || selectedAgent.id || ''));

        if (searchTerm) {
            if (hasAgentMatches) {
                const hasConversationMatches = filteredConversations.some(conversation => 
                    conversation.title.toLowerCase().includes(searchTerm.toLowerCase())
                );
                
                if (!hasConversationMatches) {
                    return filteredConversations;
                }
            }

            filteredConversations = filteredConversations.filter(conversation => 
                conversation.title.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return filteredConversations;
    };

    const getGroupedConversations = () => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);

        const filteredConversations = getFilteredConversations();
        const grouped = {
            [HistoryGroups.TODAY]: [],
            [HistoryGroups.YESTERDAY]: [],
            [HistoryGroups.LAST_WEEK]: [],
            [HistoryGroups.LAST_MONTH]: [],
            [HistoryGroups.MORE]: []
        };

        filteredConversations.forEach(conversation => {
            const conversationDate = new Date(conversation.last_modified);
            
            if (conversationDate >= today) {
                grouped[HistoryGroups.TODAY].push(conversation);
            } else if (conversationDate >= yesterday) {
                grouped[HistoryGroups.YESTERDAY].push(conversation);
            } else if (conversationDate >= lastWeek) {
                grouped[HistoryGroups.LAST_WEEK].push(conversation);
            } else if (conversationDate >= lastMonth) {
                grouped[HistoryGroups.LAST_MONTH].push(conversation);
            } else {
                grouped[HistoryGroups.MORE].push(conversation);
            }
        });

        return grouped;
    };

    useEffect(() => {
        const loadConversations = async () => {
            const agentUuid = selectedAgent?.uuid || selectedAgent?.id;
            const userUuid = authStore.userId || historyAgentStore.user_id;
            
            if (agentUuid && userUuid) {
                setIsLoadingConversations(true);
                try {
                    // Clear selected conversation when agent changes
                    historyAgentStore.setSelectedConversationId(null);
                    // Fetch conversations for the selected agent using the new endpoint
                    await historyAgentStore.fetchConversationsHTTP(agentUuid, userUuid);
                } catch (error) {
                    console.error('Error loading conversations:', error);
                } finally {
                    setIsLoadingConversations(false);
                }
            } else {
                // Clear conversations if no agent or user selected
                if (historyAgentStore.conversations) {
                    historyAgentStore.conversations = [];
                }
            }
        };

        loadConversations();
    }, [selectedAgent?.uuid, selectedAgent?.id, authStore.userId, historyAgentStore.user_id]);

    const renderGroups = () => {
        const groupedConversations = getGroupedConversations();
        
        return Object.entries(groupedConversations).map(([group, groupConversations]) => {
            if (groupConversations.length === 0) return null;
            
            return (
                <ConversationGroup key={group}>
                    <GroupTitle>{group}</GroupTitle>
                    {groupConversations.map((conversation) => (
                        <HistoryWrapper 
                            key={conversation.id} 
                            onClick={() => handleConversationClick(conversation.id)}
                            className={selectedConversationId === conversation.id ? 'active' : ''}
                        >
                            <HistoryText>{conversation.title}</HistoryText>
                        </HistoryWrapper>
                    ))}
                </ConversationGroup>
            );
        });
    };

    if (isLoadingConversations) {
        return (
            <LoadingContainer>
                <CircularProgress size={24} />
            </LoadingContainer>
        );
    }

    if (!isLoadingConversations && !conversations?.length) {
        return (
            <EmptyHistoryMessage>
                No history available for this agent
            </EmptyHistoryMessage>
        );
    }

    return (
        <HistoryContainer>
            {renderGroups()}
        </HistoryContainer>
    );
});

export default HistoryContent; 